import { Check, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import {
  type CQCPracticeSearchResult,
  useQueryCQCPractices,
} from '@/hooks/query/useQueryCQCPractices';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { FormattedMessage, useIntl } from 'react-intl';

import { organisationDetailsStepMessages } from './organisation-details-step.messages';

export interface CQCPracticeAutocompleteProps {
  value: string;
  onValueChange: (value: string) => void;
  onPracticeSelect?: (isOfficial: boolean, practiceId?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 300;

export function CQCPracticeAutocomplete({
  value,
  onValueChange,
  onPracticeSelect,
  placeholder,
  disabled,
  className,
}: CQCPracticeAutocompleteProps) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedPracticeId, setSelectedPracticeId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const debouncedSetQuery = useDebouncedCallback((query: string) => {
    setDebouncedQuery(query);
  }, DEBOUNCE_MS);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onValueChange(newValue);
      debouncedSetQuery(newValue);
      setIsOpen(true);
      if (selectedPracticeId) {
        setSelectedPracticeId(null);
      }
      onPracticeSelect?.(false, undefined);
    },
    [onValueChange, debouncedSetQuery, selectedPracticeId, onPracticeSelect]
  );

  const { data, isLoading, isFetching } = useQueryCQCPractices(debouncedQuery, isOpen);

  const practices = useMemo(() => data?.results ?? [], [data]);

  const handleSelect = useCallback(
    (practice: CQCPracticeSearchResult) => {
      onValueChange(practice.name);
      setSelectedPracticeId(practice.id);
      setIsOpen(false);
      onPracticeSelect?.(true, practice.id);
    },
    [onValueChange, onPracticeSelect]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const showLoading = isLoading || isFetching;
  const showResults =
    isOpen && debouncedQuery.length >= MIN_SEARCH_LENGTH && practices.length > 0;
  const showNoResults =
    isOpen &&
    !showLoading &&
    debouncedQuery.length >= MIN_SEARCH_LENGTH &&
    practices.length === 0;

  return (
    <div className={cn('relative w-full', className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => value.length >= MIN_SEARCH_LENGTH && setIsOpen(true)}
          placeholder={
            placeholder ??
            intl.formatMessage(
              organisationDetailsStepMessages.practiceSearchInputPlaceholder
            )
          }
          disabled={disabled}
          className="w-full rounded-md"
          aria-expanded={isOpen}
          aria-controls={listId}
          aria-autocomplete="list"
        />
        {showLoading && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="size-4 animate-spin text-text-secondary" />
          </div>
        )}
      </div>

      {(showResults || showNoResults) && (
        <div
          ref={dropdownRef}
          id={listId}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-surface shadow-lg"
          role="listbox"
        >
          {showNoResults && (
            <div className="px-3 py-2 text-sm text-text-secondary">
              <FormattedMessage
                {...organisationDetailsStepMessages.practiceSearchNoResults}
              />
            </div>
          )}

          {showResults &&
            practices.map((practice) => (
              <Button
                key={practice.id}
                size="lg"
                type="button"
                variant="ghost"
                role="option"
                aria-selected={selectedPracticeId === practice.id}
                className={cn(
                  'flex h-auto w-full cursor-pointer items-center justify-between rounded-none px-3 py-2 text-left',
                  selectedPracticeId === practice.id && 'bg-surface-hover'
                )}
                onClick={() => handleSelect(practice)}
              >
                <span className="text-sm">{practice.name}</span>
                {selectedPracticeId === practice.id && (
                  <Check className="size-4 shrink-0 text-text-primary" />
                )}
              </Button>
            ))}
        </div>
      )}
    </div>
  );
}
